import React, { CSSProperties, FC, useReducer } from 'react';
import { ReactComponent as CheckMarkSvg } from 'src/assets/icons/selectedChk.svg';
import styled, { css, useTheme } from 'styled-components';
import { typography } from 'src/common/styles/typography';
import { useDerivedState } from 'src/lib/useDerivedState';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { RefCallBack } from 'react-hook-form/dist/types';
import TruncateMarkup from 'react-truncate-markup';
import { CleanButton } from 'src/common/components/Button';
import { widthBreakpoints } from 'src/common/styles/breakpoints';
import { useMedia } from 'react-use';
import CheckboxAsterisk from '@tenant/components/CheckboxAsterisk';
import { FormattedMessage } from 'react-intl';

const ShowMoreButton = styled(CleanButton)`
  font-weight: bold;
  font-size: 0.75rem;
  line-height: 1.5rem;
  color: ${({ theme }) => theme.button.ShowMore.default.color};

  &:hover {
    text-decoration: underline;
  }
`;

const ShowLessButton = styled(ShowMoreButton)`
  display: block;
`;

const InvisibleCheckboxInput = styled.input.attrs({ type: 'checkbox' })`
  cursor: inherit;
  height: 100%;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: 24px;
  z-index: 1;

  &:focus + svg {
    outline: 2px solid ${({ theme }) => theme.colors.focus};
    outline-offset: 1px;
  }
`;

const CheckboxInput = styled(InvisibleCheckboxInput)<{ error?: boolean }>`
  ${(props) =>
      props.disabled &&
      css`
        & + svg #border,
        & + svg #checkmark,
        & + svg #not-checked {
            //stroke: ${({ theme }) => theme.text.primary};
        }
      `}
`;

export const CheckboxInputWrapper = styled.div<{
  disabled?: boolean;
  $label?: boolean;
}>`
  position: relative;
  padding: ${({ $label }) => ($label ? '0 0.9375rem 0 0' : '0')};
  display: flex;

  ${(props) =>
      !props.disabled &&
      css`
        cursor: pointer;
      `}
`;

export const CheckboxLabelText = styled.span<{ error?: boolean }>`
  color: ${({ theme, error }) =>
      error ? theme.colors.dangerBorder : theme.checkbox.default.labelColor};
  font-family: ${typography.fontFamilyDefault};
  font-size: 14px;
  user-select: none;

  a,
  a:hover,
  a:active {
    text-decoration: none;
    color: inherit;
  }
`;

export const CheckboxRect = styled.rect<{ error?: boolean }>`
  stroke: ${({ theme, error }) =>
      error ? theme.colors.dangerBorder : theme.checkbox.default.borderColor};
  transition: all 0.3;
`;

const Label = styled.label<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: start;
  vertical-align: middle;

  ${(props) =>
      !props.disabled &&
      css`
        cursor: pointer;
      `};

  ${(props) =>
      props.disabled &&
      css`
        opacity: 0.6;
      `};
`;

export interface CheckboxProps {
  className?: string;
  disabled?: boolean;
  error?: boolean;
  initialValue?: boolean;
  label?: string | React.ReactNode;
  field?: ControllerRenderProps;
  required?: boolean;
  name?: string;
  onChange?: (v: boolean) => void;
  size?: number;
  style?: CSSProperties;
  value?: boolean;
  inputRef?: RefCallBack;
  isError?: boolean;
  truncateLabel?: boolean;
}

export const Checkbox: FC<CheckboxProps> = ({
                                              className,
                                              disabled,
                                              initialValue,
                                              label = '',
                                              truncateLabel = false,
                                              onChange,
                                              size = 20,
                                              style,
                                              value,
                                              inputRef,
                                              required,
                                              error,
                                            }) => {
  const [active, setActive] = useDerivedState(initialValue ?? false, {
    value,
    onChange,
  });

  const [fullLabel, toggleFullLabel] = useReducer(
      (previous) => !previous,
      false
  );

  const isDesktop = useMedia(`(min-width: ${widthBreakpoints.px992}px)`);

  const cssTheme = useTheme();

  return (
      <Label className={className} style={style} disabled={disabled}>
        <CheckboxInputWrapper disabled={disabled} $label={!!label}>
          <CheckboxInput
              checked={active}
              onChange={() => setActive((currentValue: boolean) => !currentValue)}
              disabled={disabled}
              ref={inputRef}
              error={error}
          />
          <svg
              width={size}
              height={size}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
          >
            <CheckboxRect
                error={error}
                color={active ? cssTheme.checkbox.default.fillColor : '#fff'}
                width="22"
                height="22"
                x="1"
                strokeWidth="2"
                y="1"
                rx="2"
                id="not-checked"
            />

            {active && (
                <CheckMarkSvg color={cssTheme.checkbox.default.fillColor} />
            )}
          </svg>
          {required && <CheckboxAsterisk />}
        </CheckboxInputWrapper>
        <CheckboxLabelText error={error}>
          <TruncateMarkup
              lines={!truncateLabel || fullLabel ? 1200 : isDesktop ? 1 : 2}
              ellipsis={
                <>
                  <ShowMoreButton
                      onClick={(e) => {
                        e.preventDefault();
                        toggleFullLabel();
                      }}
                  >
                    <FormattedMessage id="common.more" />
                  </ShowMoreButton>
                </>
              }
          >
          <span>
            {label}
            {fullLabel && (
                <ShowLessButton
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFullLabel();
                    }}
                >
                  <FormattedMessage id="common.less" />
                </ShowLessButton>
            )}
          </span>
          </TruncateMarkup>
        </CheckboxLabelText>
      </Label>
  );
};
